import moment from "moment"

import {
  OrganisationalSummary,
  OrganisationalSummaryCategory,
  OrganisationalSummaryTrendDuration,
  PaymentCurrencyType,
  OrganisationalSummaryMainTrend,
  OrganisationalSummaryPerksTrend,
  OrganisationalSummaryFlexiTrend,
  OrganisationalSummaryCelebrationsTrend,
  OrganisationalSummaryWellnessTrend} from "@irati/types"

import {TrendTypes} from "./enums"

type RateType = OrganisationalSummaryMainTrend
  | OrganisationalSummaryPerksTrend
  | OrganisationalSummaryFlexiTrend
  | OrganisationalSummaryCelebrationsTrend
  | OrganisationalSummaryWellnessTrend

const buildDate = (month: number, year: number, duration: OrganisationalSummaryTrendDuration) => {
  const monthText = duration === OrganisationalSummaryTrendDuration.SummaryTrendMonthly
    ? moment().month(month - 1).format("MMM")
    : `${moment().month(month - 3).format("MMM")} - ${moment().month(month - 1).format("MMM")}`
  return `${monthText}-${moment().year(year).format("YY")}`

}

const getEngagementRate = (engagedUsers?: number, activatedUsers?: number) => {
  if (engagedUsers && activatedUsers) {
    const prevValue = engagedUsers / activatedUsers * 100
    return prevValue > 100 ? 100 : prevValue
  }
  return 0
}

const buildMainValue = (rate: OrganisationalSummaryMainTrend, trend: TrendTypes, currency: PaymentCurrencyType) => {
  if (trend === TrendTypes.engagementRate) {
    return getEngagementRate(rate.numberOfEngagedUsers?.value, rate.numberOfActivatedUsers?.value)
  }
  if (trend === TrendTypes.usersLoggedIn) {
    return rate.numberOfUsersLoggedIn?.value || 0
  }
  if (trend === TrendTypes.engagements) {
    return rate.numberOfEngagements?.value || 0
  }
  if (trend === TrendTypes.savings) {
    return currency === PaymentCurrencyType.Aud ? rate.savingsAUD?.value || 0 : rate.savingsGBP?.value || 0
  }
  if (trend === TrendTypes.savingsPercentage) {
    return rate.savingsPercentage?.value || 0
  }
}

const buildPerksValue = (rate: OrganisationalSummaryPerksTrend, trend: TrendTypes, currency: PaymentCurrencyType) => {
  if (trend === TrendTypes.engagementRate) {
    return getEngagementRate(rate.usersRedeemed?.value, rate.numberOfActivatedUsers?.value)
  }
  if (trend === TrendTypes.usersRedeemed) {
    return rate.usersRedeemed?.value || 0
  }
  if (trend === TrendTypes.redemptions) {
    return rate.redemptions?.value || 0
  }
  if (trend === TrendTypes.savings) {
    return currency === PaymentCurrencyType.Aud ? rate.savingsAUD?.value || 0 : rate.savingsGBP?.value || 0
  }
  if (trend === TrendTypes.savingsPercentage) {
    return rate.savingsPercentage?.value || 0
  }
}

const buildFlexiValue = (rate: OrganisationalSummaryFlexiTrend, trend: TrendTypes, currency: PaymentCurrencyType) => {
  if (trend === TrendTypes.engagementRate) {
    return getEngagementRate(rate.usersRedeemed?.value, rate.numberOfActivatedUsers?.value)
  }
  if (trend === TrendTypes.usersRedeemed) {
    return rate.usersRedeemed?.value || 0
  }
  if (trend === TrendTypes.redemptions) {
    return rate.redemptions?.value || 0
  }
  if (trend === TrendTypes.earned) {
    return rate.earned?.value || 0
  }
  if (trend === TrendTypes.burned) {
    return rate.burned?.value || 0
  }
  if (trend === TrendTypes.savings) {
    return currency === PaymentCurrencyType.Aud ? rate.savingsAUD?.value || 0 : rate.savingsGBP?.value || 0
  }
  if (trend === TrendTypes.savingsPercentage) {
    return rate.savingsPercentage?.value || 0
  }
}

const buildCelebrationValue = (rate: OrganisationalSummaryCelebrationsTrend, trend: TrendTypes) => {
  if (trend === TrendTypes.engagementRate) {
    return getEngagementRate(rate.usersEngaged?.value, rate.numberOfActivatedUsers?.value)
  }
  if (trend === TrendTypes.usersEngaged) {
    return rate.usersEngaged?.value || 0
  }
  if (trend === TrendTypes.recognitions) {
    return rate.recognitions?.value || 0
  }
  if (trend === TrendTypes.polls) {
    return rate.polls?.value || 0
  }
  if (trend === TrendTypes.rewardPointsGiven) {
    return rate.rewardPointsGiven?.value || 0
  }
  if (trend === TrendTypes.rewardPointsPurchased) {
    return rate.rewardPointsPurchased?.value || 0
  }
  if (trend === TrendTypes.usersGivenRecognition) {
    return rate.usersGivenRecognition?.value || 0
  }
  if (trend === TrendTypes.usersReceivedRecognition) {
    return rate.usersReceivedRecognition?.value || 0
  }
}

const buildWellness = (rate: OrganisationalSummaryWellnessTrend, trend: TrendTypes) => {
  if (trend === TrendTypes.engagementRate) {
    return getEngagementRate(rate.usersEngaged?.value, rate.numberOfActivatedUsers?.value)
  }
  if (trend === TrendTypes.usersEngaged) {
    return rate.usersEngaged?.value || 0
  }
  if (trend === TrendTypes.views) {
    return rate.views?.value || 0
  }
  if (trend === TrendTypes.webinarRegistrations) {
    return rate.webinarRegistrations?.value || 0
  }
}

const getData = (res: OrganisationalSummary, category: OrganisationalSummaryCategory) => {
  if (category === OrganisationalSummaryCategory.Perks) {
    return res?.perks?.trends
  }
  if (category === OrganisationalSummaryCategory.FlexiPerks) {
    return res?.flexi?.trends
  }
  if (category === OrganisationalSummaryCategory.Celebrations) {
    return res?.celebrations?.trends
  }
  if (category === OrganisationalSummaryCategory.Wellness) {
    return res?.wellness?.trends
  }
  return res?.main?.trends
}

const getValue = (
  rate: RateType,
  category: OrganisationalSummaryCategory,
  trend: TrendTypes,
  currency: PaymentCurrencyType) => {
  if (category === OrganisationalSummaryCategory.Perks) {
    return buildPerksValue(rate, trend, currency)
  }
  if (category === OrganisationalSummaryCategory.FlexiPerks) {
    return buildFlexiValue(rate, trend, currency)
  }
  if (category === OrganisationalSummaryCategory.Celebrations) {
    return buildCelebrationValue(rate, trend)
  }
  if (category === OrganisationalSummaryCategory.Wellness) {
    return buildWellness(rate, trend)
  }
  return buildMainValue(rate, trend, currency)
}

export default (
  res: OrganisationalSummary,
  category: OrganisationalSummaryCategory,
  trend: TrendTypes,
  currency: PaymentCurrencyType,
  duration: OrganisationalSummaryTrendDuration) => {

  const data = getData(res, category)

  if (!data) {
    return []
  }

  return data.map((rate: RateType) => {
    return {
      date: buildDate(rate.date?.month?.value, rate.date?.year?.value, duration),
      value: getValue(rate, category, trend, currency)}
  })
}
